import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { Table } from "reactstrap";
import { SelectField } from "../../SelectField/SelectField";
import { normalizeDialCode } from "../../../util/dialCode";
import CountrySelectService from "./CountrySelectService";
import { I18nContext } from "../../../store/context/i18nContext";

const CountrySelect = ({
  isd,
  isoCode,
  noLabel,
  innerContainerClassName,
  innerContainerStyle,
  containerStyle,
  error,
  inputGroup,
  style,
  name,
  plain,
  placeholder,
  className,
  onChange,
  value,
  showErr,
  outline,
  inline,
  borderNone,
  RightComponent,
  onClickRight,
  label,
  required,
  showClear,
  noIP,
  dropDownMenuWidth,
  ...props
}) => {

  const [COUNTRIES, setCountries] = useState([]);
  const { t } = useContext(I18nContext);
  const [ipCountry, setIPCountry] = useState(null);
  const onChangeRef = useRef(onChange);
  const hasAttemptedIpLookupRef = useRef(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);


  useEffect(() => {
    const fetch = async () => {
      try {
        await CountrySelectService.fetch();
        setCountries(
          toJS(CountrySelectService.countries)
            .filter((country) => !isd || Boolean(country?.isdCodes?.length || country?.isd))
            .flatMap((country) => {
              const dialCodes = isd
                ? (country?.isdCodes?.length ? country.isdCodes : country?.isd ? [country.isd] : [])
                : [country?.isd];

              return dialCodes.map((dialCode) => ({
                ...country,
                isd: dialCode || country?.isd,
                value: isd ? (dialCode || country?.isd) : isoCode ? country?.isoCode2 : country?.name,
                menuLabel: isd || isoCode ? (
                  <div className="d-flex align-items-center">
                    <div className="px-0 mx-0 d-flex justify-content-start" />
                    <div
                      style={{
                        minWidth: '30px',
                        fontSize: "14px",
                        marginRight: "6px"
                      }}
                    >
                      {dialCode || country?.isd ? `+${dialCode || country?.isd}` : ""}
                    </div>
                    <div
                      className="d-flex justify-content-start"
                      style={{
                        minWidth: '60px',
                        fontSize: "14px"
                      }}
                    >
                      {`${country?.name} (${country?.isoCode2})`}
                    </div>
                  </div>
                ) : name ? (
                  <div>
                    {`${country.name}`}
                  </div>
                ) : (
                  <div>
                    {` ${country.name}`}
                  </div>
                ),
                label: isd ? (
                  <div className="d-flex align-items-center">
                    <div className="px-0 mx-0 d-flex justify-content-start" style={{
                      minWidth: '20px'
                    }} />
                    <div className="mx-1" style={{
                      minWidth: '30px',
                      fontSize: "14px"
                    }}>
                      {dialCode || country?.isd ? `+${dialCode || country?.isd}` : ""}
                    </div>
                  </div>
                ) : isoCode ? (
                  <div className="d-flex align-items-center">
                    <div className="mx-1" style={{
                      minWidth: '30px',
                      fontSize: "14px"
                    }}>
                      {country?.isd ? `+${country?.isd}` : ""}
                    </div>
                    <div className="d-flex justify-content-start" style={{
                      minWidth: '60px'
                    }}>
                      {`${country?.name} (${country?.isoCode2})`}
                    </div>
                  </div>
                ) : name ? (
                  <div>
                    {`${country.name}`}
                  </div>
                ) : (
                  <div>
                    {` ${country.name}`}
                  </div>
                ),
              }));
            })
        );
      } catch (error) {
      }
    };
    fetch();
  }, [isd, isoCode, name]);

  const renderFunc = ({ data, innerRef, innerProps }) => {
    return (
      <div
        ref={innerRef}
        {...innerProps}
      >
        <Table style={{ margin: "0px" }} className="dropdown-hover">
          <thead style={{ margin: "0px" }}>
            <tr style={{ margin: "0px" }}>
              <td className="col-3 py-3">
                {data.flag ? (
                  <img
                    src={data.flag}
                    alt="flag"
                    style={{
                      display: "inline",
                      maxHeight: "15px",
                      margin: "0px 5px",
                    }}
                  />
                ) : null}
              </td>
              <td className="col-3 py-3">{data?.isd ? `+${data.isd}` : ""}</td>
              <td className="col-6 py-3">{`${data.name}`}</td>
            </tr>
          </thead>
        </Table>
      </div>
    );
  };

  const formatOptionLabel = ({ label, menuLabel }, { context }) => {
    return context === 'menu' ? menuLabel : label;
  };


  const filterOption = useCallback((option, inputValue) =>
    !inputValue ||
    ["isd", "name", "isoCode2"]?.some(
      key =>
        `${option.data[key]}`?.toLowerCase().includes(inputValue.toLowerCase())
    ),
    []);


  useEffect(() => {
    const fetchCountryCode = async () => {
      if (!value && isd && !noIP && !hasAttemptedIpLookupRef.current) {
        hasAttemptedIpLookupRef.current = true;
        try {
          const countryCode = normalizeDialCode(await CountrySelectService.fetchIpCallingCode());
          if (!countryCode) {
            throw new Error("Missing country calling code");
          }
          onChangeRef.current?.({ value: countryCode });
          setIPCountry(countryCode);
        } catch (error) {
          console.error("Error fetching country code:", error);
          const fallbackCountryCode = "86";
          onChangeRef.current?.({ value: fallbackCountryCode }); // Default to 86 if fetch fails
          setIPCountry(fallbackCountryCode);
        }
      }
    };
    fetchCountryCode();
  }, [value, isd, noIP]);


  return (
    <React.Fragment>
      {COUNTRIES.length > 0 && (

        <SelectField
          selectTheme={props?.selectTheme}
          formatOptionLabel={formatOptionLabel}
          render={renderFunc}
          filterOption={filterOption}
          showClear={showClear}
          key={value}
          label={label ? label : t("Country")}
          error={
            error
          }
          required={required}
          containerStyle={containerStyle}
          data={COUNTRIES}
          inputGroup={inputGroup}
          innerContainerStyle={innerContainerStyle}
          innerContainerClassName={innerContainerClassName}
          placeholder={placeholder ?? ""}
          noLabel={noLabel}
          style={style}
          plain={plain}
          className={className}
          onChange={onChange}
          value={value ? value : ipCountry}
          outline={outline}
          showErr={showErr}
          inline={inline}
          borderNone={borderNone}
          RightComponent={RightComponent}
          onClickRight={onClickRight}

          {...props}
        />
      )}

      {!(COUNTRIES?.length > 0) && (
        <SelectField
          selectTheme={props?.selectTheme}
          formatOptionLabel={formatOptionLabel}
          render={renderFunc}
          filterOption={filterOption}
          showClear={showClear}
          label={label ? label : t("Country")}
          isDisabled={true}
          error={
            error
          }
          containerStyle={containerStyle}
          data={COUNTRIES}
          inputGroup={inputGroup}
          dropDownMenuWidth={dropDownMenuWidth}
          innerContainerStyle={innerContainerStyle}
          innerContainerClassName={innerContainerClassName}
          className={className}
          placeholder={placeholder ?? ""}
          noLabel={noLabel}

          style={style}
          plain={plain}
          onChange={onChange}
          value={value}
          outline={outline}
          showErr={showErr}

          inline={inline}
          borderNone={borderNone}
          RightComponent={RightComponent}
          onClickRight={onClickRight}
        />
      )}
    </React.Fragment>
  );
};

export default observer(CountrySelect);
